
import React from 'react';
import { usePriceLock } from '@/hooks/price-lock/usePriceLock';
import { useDealerPurities } from '@/hooks/price-lock/useDealerPurities';
import { PurityButtons } from './PurityButtons';
import { MetalSelector } from './MetalSelector';
import { WeightInput } from './WeightInput';
import { PriceLockRequestModal } from '@/components/PriceLockRequestModal';
import { Button } from '@/components/ui/button';
import { Lock, Loader2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { Purity } from '@/lib/pricing';

interface EnhancedPriceLockCardProps {
  dealerId: string;
  dealerName: string;
  dealerSpread?: number;
  initialMetal?: 'gold' | 'silver';
}

export function EnhancedPriceLockCard({ dealerId, dealerName, dealerSpread, initialMetal }: EnhancedPriceLockCardProps) {
  const {
    metal,
    setMetal,
    weight,
    setWeight,
    goldPurity,
    silverPurity,
    isRequestModalOpen,
    setIsRequestModalOpen,
    basePrice,
    dealerPrice,
    currentSpread,
    handlePurityChange,
    handleSubmit,
    calculateEstimatedValue,
    isUpdatingPrices,
  } = usePriceLock({ dealerId, dealerSpread, initialMetal });

  const { purities: availablePurities, loading: puritiesLoading } = useDealerPurities(dealerId);

  const currentPurity = metal === 'gold' ? goldPurity : silverPurity;
  const puritiesForMetal = metal === 'gold' ? availablePurities.ORO : availablePurities.ARGENTO;

  // Effetto per reimpostare la purezza quando si cambia metallo per garantire che sia sempre selezionata una purezza valida.
  React.useEffect(() => {
    if (puritiesLoading || !puritiesForMetal || puritiesForMetal.length === 0) return;

    const isCurrentPurityAvailable = (puritiesForMetal as Purity[]).includes(currentPurity as Purity);

    if (!isCurrentPurityAvailable) {
      console.log(`[EnhancedPriceLockCard] La purezza ${currentPurity} non è disponibile per ${metal}. Reimpostazione a ${puritiesForMetal[0]}.`);
      handlePurityChange(String(puritiesForMetal[0]));
    }
  }, [metal, puritiesForMetal, puritiesLoading, currentPurity, handlePurityChange]);


  const formatPrice = (price: number) => {
    if (!price || price === 0) return 'N/D';
    return `€${price.toFixed(2)}`;
  };
  
  const formattedDealerPrice = formatPrice(dealerPrice);

  return (
    <>
      <Card className="bg-gradient-to-br from-card to-muted/20 border-border/50 shadow-lg overflow-hidden">
        <CardContent className="p-6 text-center">
          <MetalSelector metal={metal} setMetal={setMetal} disabled={isUpdatingPrices} />

          <div className="my-6 min-h-[120px] flex flex-col justify-center">
            <p className="text-sm text-muted-foreground">Prezzo di acquisto</p>
            {isUpdatingPrices ? (
              <div className="flex justify-center items-center h-16">
                 <Loader2 className="h-10 w-10 animate-spin text-primary" />
              </div>
            ) : (
              <>
                <div className="flex items-center justify-center gap-2">
                  <h2 className="text-5xl font-bold tracking-tighter text-foreground">
                    {formattedDealerPrice}
                  </h2>
                  <span className="text-lg text-muted-foreground self-end mb-1">/g</span>
                </div>
                {currentSpread !== null && currentSpread !== undefined && currentSpread !== 0 && (
                  <p className="text-xs text-green-500 font-medium">
                    Spread del {currentSpread > 0 ? '+' : ''}{currentSpread.toFixed(2)}% applicato
                  </p>
                )}
                 <p className="text-xs text-muted-foreground mt-1">Prezzo di mercato: {formatPrice(basePrice)}/g</p>
              </>
            )}
          </div>

          <div className="space-y-6">
             <div>
              <p className="text-sm font-medium mb-3">Seleziona purezza</p>
              {puritiesLoading ? (
                 <div className="flex justify-center items-center h-10">
                    <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                 </div>
              ) : (
                <PurityButtons
                  metal={metal}
                  purities={puritiesForMetal as Purity[]}
                  selectedPurity={currentPurity as Purity}
                  onPurityChange={(p) => handlePurityChange(String(p))}
                  disabled={isUpdatingPrices}
                />
              )}
            </div>

            <WeightInput weight={weight} setWeight={setWeight} />
            
            <div className="pt-2">
                <div className="mb-4 text-center">
                  <p className="text-sm text-muted-foreground">Valore stimato</p>
                  <p className="text-3xl font-bold tracking-tight">
                    €{calculateEstimatedValue().toFixed(2)}
                  </p>
                </div>
                <Button className="w-full bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg" size="lg" onClick={handleSubmit} disabled={isUpdatingPrices}>
                    <Lock className="h-5 w-5 mr-2" />
                    Blocca questo prezzo per 24h
                </Button>
            </div>
          </div>
        </CardContent>
      </Card>
      
      <PriceLockRequestModal
        isOpen={isRequestModalOpen}
        onClose={() => setIsRequestModalOpen(false)}
        dealerId={dealerId}
        dealerName={dealerName}
        metal={metal === 'gold' ? 'ORO' : 'ARGENTO'}
        purity={String(currentPurity)}
        weight={parseFloat(weight) || 0}
        estimatedValue={calculateEstimatedValue()}
        pricePerGram={dealerPrice}
      />
    </>
  );
}
